<template>
  <div class="feedback-and-share-popup">
    <div v-if="canBeShown" :class="{ popup: true, show }">
      <div
        :class="{
          'd-flex': true,
          'justify-content-between': true,
          'align-items-center': showFeedbackView && !showCommentField,
          'align-items-start': !showFeedbackView || showCommentField,
        }"
      >
        <div>
          <div :class="{ 'feedback-view': true, show: showFeedbackView }">
            <p v-if="error" class="text-danger">
              {{ error }}
            </p>

            <h5 v-if="user.firstName">
              {{
                $i18n.feedbackAndShare.titleWithName.replace(
                  '%NAME%',
                  user.firstName
                )
              }}
            </h5>
            <h5 v-else>{{ $i18n.feedbackAndShare.title }}</h5>

            <div :class="{ emojis: true, disabled: !!userFeedback.emotion }">
              <span
                v-for="item in feedback"
                :key="item.key"
                :title="item.helptext"
                :class="{
                  active: userFeedback.emotion == item.key,
                  inactive:
                    userFeedback.emotion !== null &&
                    userFeedback.emotion !== item.key,
                }"
                @click="() => onEmotion(item.key, item.helptext)"
              >
                <img :src="`/static/img/feedback-and-share/${item.emoji}`" />
              </span>
            </div>

            <div :class="{ comment: true, show: showCommentField }">
              <textarea
                v-model="userFeedback.comment"
                :placeholder="$i18n.feedbackAndShare.commentText"
                rows="2"
              />
              <button
                class="btn btn-success btn-sm float-end"
                @click="onComment"
              >
                {{ $i18n.feedbackAndShare.sendButton }}
              </button>
            </div>
          </div>
          <div :class="{ 'share-view': true, show: showShareView }">
            <p>
              <b>{{ $i18n.feedbackAndShare.payoffText }}</b>
            </p>
            <div v-if="userFeedback.emotion > 3" class="share-section">
              <p>{{ $i18n.feedbackAndShare.shareText }}</p>
              <a
                href="https://g.page/r/CTI2LhoFW3wsEAg/review"
                class="btn btn-primary mb-3 d-inline-flex align-items-center"
                target="_blank"
                onclick="_paq.push(['trackEvent', 'FeedbackAndShare', 'SocialShare', 'GoogleReview']);"
              >
                {{ $i18n.feedbackAndShare.reviewButton }}
                <IconExternalLink class="ms-2 white" />
              </a>
              <p>{{ $i18n.feedbackAndShare.shareText2 }}</p>
              <div class="share-buttons">
                <a
                  :href="`mailto:?subject=${encodeURIComponent(
                    $i18n.feedbackAndShare.shareButtonTitle
                  )}&body=${encodeURIComponent(
                    $i18n.feedbackAndShare.shareButtonText +
                      '\n\n ' +
                      getShareLink('email')
                  )}`"
                  target="_blank"
                  title="Send email"
                  onclick="_paq.push(['trackEvent', 'FeedbackAndShare', 'SocialShare', 'Email']);"
                >
                  <email />
                </a>
                <a
                  :href="`whatsapp://send?text=${encodeURIComponent(
                    $i18n.feedbackAndShare.shareButtonTitle +
                      '\n\n' +
                      $i18n.feedbackAndShare.shareButtonText +
                      '\n\n' +
                      getShareLink('whatsapp')
                  )}`"
                  data-action="share/whatsapp/share"
                  onclick="_paq.push(['trackEvent', 'FeedbackAndShare', 'SocialShare', 'WhatsApp']);"
                >
                  <whatsapp />
                </a>
                <a
                  :href="`https://twitter.com/intent/tweet?source=${encodeURIComponent(
                    getShareLink('twitter')
                  )}&text=${encodeURIComponent(
                    $i18n.feedbackAndShare.shareButtonTitle +
                      '\n\n' +
                      $i18n.feedbackAndShare.shareButtonText +
                      '\n\n' +
                      getShareLink('twitter')
                  )}`"
                  target="_blank"
                  title="Tweet"
                  onclick="window.open(this.href, 'twitterwindow', 'left=20,top=20,width=600,height=300,toolbar=0,resizable=1'); _paq.push(['trackEvent', 'FeedbackAndShare', 'SocialShare', 'Twitter']); return false;"
                >
                  <twitter />
                </a>
                <a
                  :href="`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                    getShareLink('facebook')
                  )}&quote=${encodeURIComponent(
                    $i18n.feedbackAndShare.shareButtonTitle +
                      '\n\n' +
                      $i18n.feedbackAndShare.shareButtonText
                  )}`"
                  title="Share on Facebook"
                  target="_blank"
                  onclick="window.open(this.href, 'facebookwindow','left=20,top=20,width=600,height=700,toolbar=0,resizable=1'); _paq.push(['trackEvent', 'FeedbackAndShare', 'SocialShare', 'Facebook']); return false;"
                >
                  <facebook />
                </a>
              </div>
            </div>
          </div>
        </div>
        <button class="btn btn-link close-button" @click="onClose">
          <close />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import account from '../../components/account';
import { isLocalStorageAvailable } from '../../util/localStorage';

import close from '../../../../templates/icons/close.svg';
import email from '../../../../templates/icons/email-fill.svg';
import whatsapp from '../../../../templates/icons/whatsapp-fill.svg';
import facebook from '../../../../templates/icons/facebook-fill.svg';
import twitter from '../../../../templates/icons/twitter-fill.svg';
import IconExternalLink from '../../../../templates/icons/external-link.svg';

export default {
  name: 'FeedbackAndShare',
  components: {
    close,
    email,
    whatsapp,
    facebook,
    twitter,
    IconExternalLink,
  },
  data() {
    return {
      show: false,
      showFeedbackView: true,
      showShareView: false,
      error: null,
      user: {
        isAuthenticated: account.isAuthenticated(),
        firstName: account.isAuthenticated()
          ? account.getProfile().firstName
          : null,
      },
      feedback: [
        { key: 1, emoji: 'angry.svg', helptext: 'Strongly dislike' },
        { key: 2, emoji: 'thinking.svg', helptext: 'Not sure yet/Doubting' },
        { key: 3, emoji: 'neutral.svg', helptext: 'Not great' },
        { key: 4, emoji: 'smile.svg', helptext: 'I like it!' },
        {
          key: 5,
          emoji: 'starstruck.svg',
          helptext: 'Love it, keep it going!',
        },
      ],
      userFeedback: {
        emotion: null,
        comment: '',
      },
      databaseId: null,
      showCondition: {
        // hasBeenBelowHalfOfPage: false,
        // maxScroll:
        //   Math.max(
        //     document.body.scrollHeight,
        //     document.body.offsetHeight,
        //     document.documentElement.clientHeight,
        //     document.documentElement.scrollHeight,
        //     document.documentElement.offsetHeight
        //   ) - window.innerHeight,
        startTime: Date.now(),
      },
    };
  },
  computed: {
    canBeShown() {
      if (!isLocalStorageAvailable) {
        return false;
      }

      // Popup can't/shouldn't be shown when the visitor already interacted with it,
      if (localStorage.getItem('hide-feedback-share-popup') === 'true') {
        return false;
      }

      // Not before the 10th page visit
      const pageCount = this.getAndSetPageCount();
      if (pageCount < 10) {
        return false;
      }

      // Don't show before the cookiebar has been accepted, might be annoying
      // otherwise
      if (!window.cookies.hasConsent()) {
        return false;
      }

      // Or when the giftcard modal is on the page and hasn't been shown yet
      const modal = document.getElementById('catalog-signup-modal');
      if (modal && window.cookies.getCookie('popup-display') !== 'true') {
        return false;
      }

      return true;
    },
    showCommentField() {
      return this.userFeedback.emotion !== null;
    },
  },
  mounted() {
    // Don't start the check interval when the popup shouldn't be shown anyway
    // if (!this.canBeShown) {
    //   window.showSendInBlue();
    //   return;
    // }

    // Check every second if the visitor scrolled enough and has been on the page for long enough
    this.checkShowInterval = setInterval(() => {
      this.checkShowCondition();
    }, 1000);
  },
  methods: {
    getShareLink(source) {
      return `${this.$cece.domain}?mtm_campaige=feedback-and-share&mtm_source=${source}`;
    },
    getAndSetPageCount() {
      let count = 0;
      if (localStorage.getItem('feedback-page-count') !== null) {
        count = parseInt(localStorage.getItem('feedback-page-count'));
      }
      if (Number.isNaN(count)) {
        count = 0;
      }
      count++;
      localStorage.setItem('feedback-page-count', count);
      return count;
    },
    checkShowCondition() {
      // Show when the show conditions in canBeShown are fulfilled and
      // the visitor has been on the page for at least 5 seconds
      if (
        this.canBeShown &&
        // this.hasBeenBelowHalfOfPage() &&
        this.showCondition.startTime < Date.now() - 5000
      ) {
        this.showPopup();
        clearInterval(this.checkShowInterval);
      }
    },
    // hasBeenBelowHalfOfPage() {
    //   if (!this.showCondition.hasBeenBelowHalfOfPage) {
    //     this.showCondition.hasBeenBelowHalfOfPage =
    //       window.pageYOffset > this.showCondition.maxScroll * 0.4;
    //   }
    //   return this.showCondition.hasBeenBelowHalfOfPage;
    // },
    showPopup() {
      this.show = true;
      _paq.push(['trackEvent', 'FeedbackAndShare', 'Show popup']);
    },
    async onEmotion(key, value) {
      // For now only allow replying once
      if (this.userFeedback.emotion) {
        return;
      }

      this.userFeedback.emotion = key;
      localStorage.setItem('hide-feedback-share-popup', 'true');

      try {
        const response = await fetch('/api/user-feedback/', {
          method: 'POST',
          body: JSON.stringify({
            emotion: key,
            comment: this.userFeedback.comment,
            url: window.location.href,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        this.databaseId = data.id;
      } catch (err) {
        // No error as the user might not know we are already saving here
      }

      _paq.push(['trackEvent', 'FeedbackAndShare', 'Save emotion', value]);
    },
    async onComment() {
      localStorage.setItem('hide-feedback-share-popup', 'true');

      try {
        const response = await fetch('/api/user-feedback/', {
          method: this.databaseId ? 'PUT' : 'POST',
          body: JSON.stringify({
            id: this.databaseId,
            emotion: this.userFeedback.emotion,
            comment: this.userFeedback.comment,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          this.showFeedbackView = false;
          this.showShareView = true;
        } else {
          this.error = this.$i18n.feedbackAndShare.errorSavingData;
        }
      } catch (err) {
        console.error(err);
        this.error = this.$i18n.feedbackAndShare.errorSavingData;
      }

      if (this.userFeedback.comment) {
        _paq.push([
          'trackEvent',
          'FeedbackAndShare',
          'Save comment',
          this.userFeedback.comment,
        ]);
      }
    },
    async onClose() {
      this.close();
      localStorage.setItem('hide-feedback-share-popup', 'true');
      _paq.push(['trackEvent', 'FeedbackAndShare', 'Close popup']);
    },
    close() {
      this.show = false;
      clearInterval(this.checkShowInterval);

      // // Show chat
      // if (window.showSendInBlue) {
      //   window.showSendInBlue();
      // }
    },
  },
};
</script>
